import { get, isEmpty, minBy } from 'lodash';
import {
  checkDistanceBetweenCoordinates,
  areCoordinatesEqual,
} from '../../../utils/helpers';
import { getItem, removeItem, setItem } from '../../../utils/storage';
import { storageTypes } from '../../../constants/globals';
import { googleMapAPIKey } from '../../../constants/configuration';
import { getLocalityAddressFromAddressComponent } from '../../../selectors';
import { normalizeGuestLocation } from './siteOutletHelpers';

const REVERSE_GEOCODE_TIMEOUT_MS = 2500;
let googleMapsLoadPromise = null;

export const SITE_DELIVERY_LOCATION_DRAFT_KEY = 'siteDeliveryLocationDraft';
export const SITE_NEEDS_DELIVERY_LOCATION_CONFIRM_KEY = 'siteNeedsDeliveryLocationConfirm';
export const SITE_PENDING_ADD_TO_CART_KEY = 'sitePendingAddToCart';
/** Deep-link / contextual outlet: pending in-range check for address/draft vs bound restaurant. */
export const SITE_CONTEXTUAL_DELIVERY_VALIDATE_KEY = 'siteContextualDeliveryValidate';
/** Deep-link: show "Select Delivery Address" (out of range or no device geo). */
export const SITE_NEEDS_SELECT_DELIVERY_ADDRESS_KEY = 'siteNeedsSelectDeliveryAddress';

const parseAddressCoordinate = (value) => {
  if (value === null || value === undefined) {
    return NaN;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return NaN;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const getAddressLat = (address) =>
  parseAddressCoordinate(
    get(address, 'latitude', get(address, 'lat', get(address, 'location.latitude', null))),
  );

const getAddressLng = (address) =>
  parseAddressCoordinate(
    get(address, 'longitude', get(address, 'lng', get(address, 'location.longitude', null))),
  );

/**
 * Nearest saved guest address within threshold meters of device coords.
 * Uses checkDistanceBetweenCoordinates (km) × 1000 vs nearbyOutletThresoldInMeter.
 */
export const findNearbyGuestAddress = ({
  guestAddresses = [],
  guestLocation,
  nearbyOutletThresoldInMeter = 50,
}) => {
  const normalized = normalizeGuestLocation(guestLocation);
  if (!normalized || isEmpty(guestAddresses)) {
    return null;
  }

  const thresholdKm = Number(nearbyOutletThresoldInMeter) / 1000;
  const withDistance = (guestAddresses || [])
    .map((address) => {
      const lat = getAddressLat(address);
      const lng = getAddressLng(address);
      if (Number.isNaN(lat) || Number.isNaN(lng)) {
        return null;
      }
      const distanceKm = checkDistanceBetweenCoordinates(
        {
          latitude1: normalized.lat,
          longitude1: normalized.lng,
          latitude2: lat,
          longitude2: lng,
        },
        false,
      );
      return { address, distanceKm };
    })
    .filter(Boolean)
    .filter(({ distanceKm }) => distanceKm < thresholdKm);

  if (isEmpty(withDistance)) {
    return null;
  }

  return get(minBy(withDistance, 'distanceKm'), 'address', null);
};

export const getDeliveryLocationDraft = () => {
  try {
    return getItem(storageTypes.sessionStorage, SITE_DELIVERY_LOCATION_DRAFT_KEY) || null;
  } catch (e) {
    return null;
  }
};

export const setDeliveryLocationDraft = (draft) => {
  if (isEmpty(draft)) {
    removeItem(storageTypes.sessionStorage, SITE_DELIVERY_LOCATION_DRAFT_KEY);
    return;
  }
  setItem(storageTypes.sessionStorage, SITE_DELIVERY_LOCATION_DRAFT_KEY, draft);
};

export const clearDeliveryLocationDraft = () => {
  removeItem(storageTypes.sessionStorage, SITE_DELIVERY_LOCATION_DRAFT_KEY);
};

export const getNeedsDeliveryLocationConfirm = () =>
  getItem(storageTypes.sessionStorage, SITE_NEEDS_DELIVERY_LOCATION_CONFIRM_KEY) === true ||
  getItem(storageTypes.sessionStorage, SITE_NEEDS_DELIVERY_LOCATION_CONFIRM_KEY) === 'true';

export const setNeedsDeliveryLocationConfirm = (value) => {
  if (value) {
    setItem(storageTypes.sessionStorage, SITE_NEEDS_DELIVERY_LOCATION_CONFIRM_KEY, true);
  } else {
    removeItem(storageTypes.sessionStorage, SITE_NEEDS_DELIVERY_LOCATION_CONFIRM_KEY);
  }
};

export const getNeedsContextualDeliveryValidate = () =>
  getItem(storageTypes.sessionStorage, SITE_CONTEXTUAL_DELIVERY_VALIDATE_KEY) === true ||
  getItem(storageTypes.sessionStorage, SITE_CONTEXTUAL_DELIVERY_VALIDATE_KEY) === 'true';

export const setNeedsContextualDeliveryValidate = (value) => {
  if (value) {
    setItem(storageTypes.sessionStorage, SITE_CONTEXTUAL_DELIVERY_VALIDATE_KEY, true);
  } else {
    removeItem(storageTypes.sessionStorage, SITE_CONTEXTUAL_DELIVERY_VALIDATE_KEY);
  }
};

export const clearNeedsContextualDeliveryValidate = () => {
  removeItem(storageTypes.sessionStorage, SITE_CONTEXTUAL_DELIVERY_VALIDATE_KEY);
};

export const getNeedsSelectDeliveryAddress = () =>
  getItem(storageTypes.sessionStorage, SITE_NEEDS_SELECT_DELIVERY_ADDRESS_KEY) === true ||
  getItem(storageTypes.sessionStorage, SITE_NEEDS_SELECT_DELIVERY_ADDRESS_KEY) === 'true';

export const setNeedsSelectDeliveryAddress = (value) => {
  if (value) {
    setItem(storageTypes.sessionStorage, SITE_NEEDS_SELECT_DELIVERY_ADDRESS_KEY, true);
  } else {
    removeItem(storageTypes.sessionStorage, SITE_NEEDS_SELECT_DELIVERY_ADDRESS_KEY);
  }
};

export const clearNeedsSelectDeliveryAddress = () => {
  removeItem(storageTypes.sessionStorage, SITE_NEEDS_SELECT_DELIVERY_ADDRESS_KEY);
};

/**
 * Coords for validateLocalities / map center: selected address → draft only (no IP guest geo).
 */
export const getDeliveryLocationCoordsForValidate = ({
  selectedAddress,
  draft,
} = {}) => {
  if (!isEmpty(selectedAddress)) {
    const fromAddress = normalizeGuestLocation({
      lat: get(selectedAddress, 'latitude', get(selectedAddress, 'lat')),
      lng: get(selectedAddress, 'longitude', get(selectedAddress, 'lng')),
    });
    if (fromAddress) {
      return fromAddress;
    }
  }
  if (!isEmpty(draft)) {
    return normalizeGuestLocation(draft);
  }
  return null;
};

/**
 * True when selectedAddress or session draft already has usable coords.
 */
export const hasBoundDeliveryLocation = ({ selectedAddress, draft } = {}) =>
  !!getDeliveryLocationCoordsForValidate({
    selectedAddress,
    draft: draft !== undefined ? draft : getDeliveryLocationDraft(),
  });

/**
 * Resolve SiteDeliveryLocationGate `initialCoords` by flow (call-site, keep gate dumb).
 * - addToCart / changeLocation: prefer bound draft or selectedAddress (user pick / reload)
 * - other (spToDl, changeCity, first pick): prefer device guestLocation
 */
export const resolveDeliveryLocationGateInitialCoords = ({
  flowKind = '',
  selectedAddress,
  locationDraft,
  guestLocation,
} = {}) => {
  const preferBound = flowKind === 'addToCart' || flowKind === 'changeLocation';

  const draftWithCoords =
    !isEmpty(locationDraft) && normalizeGuestLocation(locationDraft)
      ? locationDraft
      : null;
  const addressWithCoords = getDeliveryLocationCoordsForValidate({
    selectedAddress,
  })
    ? selectedAddress
    : null;
  const guestWithCoords = normalizeGuestLocation(guestLocation)
    ? guestLocation
    : null;

  if (preferBound) {
    return draftWithCoords || addressWithCoords || guestWithCoords || null;
  }

  return guestWithCoords || draftWithCoords || addressWithCoords || null;
};

/**
 * Reuse an existing address/draft across reload. Soft-flags range check unless
 * the Select Delivery Address CTA is already showing.
 * @returns {'address'|'draft'|null}
 */
const tryPreserveBoundDeliveryLocation = ({ selectedAddress } = {}) => {
  if (!isEmpty(selectedAddress)) {
    const fromAddress = normalizeGuestLocation({
      lat: get(selectedAddress, 'latitude', get(selectedAddress, 'lat')),
      lng: get(selectedAddress, 'longitude', get(selectedAddress, 'lng')),
    });
    if (fromAddress) {
      if (!getNeedsSelectDeliveryAddress()) {
        setNeedsContextualDeliveryValidate(true);
      }
      return 'address';
    }
  }

  const draft = getDeliveryLocationDraft();
  if (!isEmpty(draft) && normalizeGuestLocation(draft)) {
    if (!getNeedsSelectDeliveryAddress()) {
      setNeedsContextualDeliveryValidate(true);
    }
    return 'draft';
  }

  return null;
};

/**
 * Menu/scan deep link: bind nearest saved address or reverse-geocode draft.
 * Does not rebind outlet. IP / no device geo → prompt Select Delivery Address.
 * Preserves an existing address/draft across reload unless `forceRebind`
 * (e.g. geo permission denied → granted).
 *
 * @param {string} locationSource - SITE_LOCATION_SOURCE value (`device` | `ip` | `none`)
 * @returns {Promise<'address'|'draft'|'none'>}
 */
export const hydrateContextualDeliveryLocation = async ({
  guestLocation,
  locationSource = 'none',
  guestAddresses = [],
  nearbyOutletThresoldInMeter = 50,
  dispatchSelectedAddress,
  selectedAddress,
  forceRebind = false,
  clearSelectedAddress,
}) => {
  if (forceRebind) {
    if (typeof clearSelectedAddress === 'function') {
      await clearSelectedAddress();
    }
    clearBoundDeliveryLocationSession();
  } else {
    const preserved = tryPreserveBoundDeliveryLocation({ selectedAddress });
    if (preserved) {
      return preserved;
    }
  }

  const normalized = normalizeGuestLocation(guestLocation);
  if (locationSource === 'ip' || locationSource === 'none' || !normalized) {
    clearDeliveryLocationDraft();
    setNeedsDeliveryLocationConfirm(false);
    clearNeedsContextualDeliveryValidate();
    setNeedsSelectDeliveryAddress(true);
    return 'none';
  }

  let nearbyAddress = null;
  if (!isEmpty(guestAddresses)) {
    nearbyAddress = findNearbyGuestAddress({
      guestAddresses,
      guestLocation: normalized,
      nearbyOutletThresoldInMeter,
    });
  }

  if (!isEmpty(nearbyAddress) && typeof dispatchSelectedAddress === 'function') {
    await dispatchSelectedAddress(nearbyAddress);
    clearDeliveryLocationDraft();
    setNeedsDeliveryLocationConfirm(false);
    setNeedsSelectDeliveryAddress(false);
    setNeedsContextualDeliveryValidate(true);
    return 'address';
  }

  // Coords-only until range sync — avoid reverse-geocode + bar flash when out of range.
  clearDeliveryLocationDraft();
  setNeedsDeliveryLocationConfirm(false);
  setDeliveryLocationDraft({
    lat: normalized.lat,
    lng: normalized.lng,
    formattedAddress: '',
    addressLines: [],
    city: '',
    confirmedAt: Date.now(),
  });
  setNeedsSelectDeliveryAddress(false);
  setNeedsContextualDeliveryValidate(true);
  return 'draft';
};

export const setPendingAddToCartPayload = (payload) => {
  if (isEmpty(payload)) {
    removeItem(storageTypes.sessionStorage, SITE_PENDING_ADD_TO_CART_KEY);
    return;
  }
  setItem(storageTypes.sessionStorage, SITE_PENDING_ADD_TO_CART_KEY, payload);
};

export const getPendingAddToCartPayload = () =>
  getItem(storageTypes.sessionStorage, SITE_PENDING_ADD_TO_CART_KEY) || null;

export const clearPendingAddToCartPayload = () => {
  removeItem(storageTypes.sessionStorage, SITE_PENDING_ADD_TO_CART_KEY);
};

/**
 * Clear session-bound delivery location (draft + contextual/select/pending flags).
 * Does not clear Redux selectedAddress — callers dispatch clearSelectedAddress.
 */
export const clearBoundDeliveryLocationSession = () => {
  clearDeliveryLocationDraft();
  setNeedsDeliveryLocationConfirm(false);
  clearNeedsContextualDeliveryValidate();
  clearNeedsSelectDeliveryAddress();
  clearPendingAddToCartPayload();
};

export const buildLocationDraftFromGeocode = ({
  lat,
  lng,
  formattedAddress = '',
  addressLines = [],
  city = '',
}) => ({
  lat: Number(lat),
  lng: Number(lng),
  formattedAddress,
  addressLines: addressLines || [],
  city: city || '',
  confirmedAt: Date.now(),
});

/**
 * Label parts from a Google locality detail — same rules as
 * SiteDeliveryLocationGate.applyLocationDetail / map confirm.
 */
export const getDeliveryLocalityLabelsFromDetail = (localityDetail = {}) => {
  const { address_components, guestLocation, name } = localityDetail;
  let subLocality = '';
  let localityValue = '';
  let administrativeArea = '';
  if (!isEmpty(guestLocation)) {
    subLocality = get(guestLocation, 'locality', '');
    localityValue = get(guestLocation, 'cityName', '');
  } else {
    const addressComponent = getLocalityAddressFromAddressComponent(
      address_components || [],
    );
    subLocality = get(addressComponent, 'subLocality', '');
    localityValue = get(addressComponent, 'locality', '');
    administrativeArea = get(addressComponent, 'administrativeArea', '');
  }

  const primary = name || subLocality || localityValue;
  const secondary = [
    subLocality && !isEmpty(name) ? subLocality : null,
    localityValue,
    administrativeArea,
  ]
    .filter((part) => !isEmpty(part))
    .join(', ');

  return {
    primary: primary || '',
    secondary: secondary || '',
    city: localityValue || '',
  };
};

/** Draft shape matching map confirm (city + addressLines for the outlet bar). */
export const buildDeliveryLocationDraftFromLocalityDetail = ({
  localityDetail,
  lat,
  lng,
  fallbackCity = '',
}) => {
  const { primary, secondary, city } = getDeliveryLocalityLabelsFromDetail(
    localityDetail,
  );
  const formattedAddress = [primary, secondary].filter((part) => !isEmpty(part)).join(', ');
  return buildLocationDraftFromGeocode({
    lat,
    lng,
    formattedAddress,
    addressLines: secondary
      ? [primary, secondary].filter(Boolean)
      : formattedAddress
        ? [formattedAddress]
        : [],
    city: city || fallbackCity || primary || '',
  });
};

const waitForGoogleMaps = (timeoutMs = REVERSE_GEOCODE_TIMEOUT_MS) =>
  new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.Geocoder) {
        resolve(window.google.maps);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        reject(new Error('Google Maps Geocoder unavailable'));
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });

/** Load Maps JS once (browser) — REST geocode is CORS-blocked in web. */
export const ensureGoogleMapsLoaded = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('No window'));
  }
  if (window.google && window.google.maps && window.google.maps.Geocoder) {
    return Promise.resolve(window.google.maps);
  }
  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existing) {
      waitForGoogleMaps()
        .then(resolve)
        .catch(reject);
      return;
    }

    const callbackName = `__fudrGoogleMapsGeocodeCb_${Date.now()}`;
    window[callbackName] = () => {
      try {
        delete window[callbackName];
      } catch (e) {
        // ignore
      }
      if (window.google && window.google.maps) {
        resolve(window.google.maps);
      } else {
        reject(new Error('Google Maps callback without maps'));
      }
    };

    const script = document.createElement('script');
    script.async = true;
    script.dataset.fudrGoogleMaps = 'geocode';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapAPIKey}&callback=${callbackName}`;
    script.onerror = () => {
      googleMapsLoadPromise = null;
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(script);
  }).catch((err) => {
    googleMapsLoadPromise = null;
    throw err;
  });

  return googleMapsLoadPromise;
};

/**
 * Reverse-geocode lat/lng → Google locality detail (same source as map drag).
 * Mirrors fudr-reward-app brand/massive getAddressFromCoords intent.
 */
export const reverseGeocodeLatLng = ({ lat, lng } = {}) => {
  const latitude = Number(lat);
  const longitude = Number(lng);
  if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
    return Promise.resolve(null);
  }

  const geocodeWithMaps = (maps) =>
    new Promise((resolve) => {
      try {
        const geocoder = new maps.Geocoder();
        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (
            status === (maps.GeocoderStatus && maps.GeocoderStatus.OK) ||
            status === 'OK'
          ) {
            const markedAddress = (results && results[0]) || null;
            if (markedAddress) {
              resolve({
                ...markedAddress,
                coordinate: { lat: latitude, lng: longitude },
              });
              return;
            }
          }
          resolve(null);
        });
      } catch (e) {
        resolve(null);
      }
    });

  let timeoutId;
  const timeoutPromise = new Promise((resolve) => {
    timeoutId = setTimeout(() => resolve(null), REVERSE_GEOCODE_TIMEOUT_MS);
  });

  return Promise.race([
    ensureGoogleMapsLoaded()
      .then(geocodeWithMaps)
      .then((result) => {
        clearTimeout(timeoutId);
        return result;
      })
      .catch(() => {
        clearTimeout(timeoutId);
        return null;
      }),
    timeoutPromise,
  ]);
};

/**
 * Launch / geo-only Delivery draft: reverse-geocode like Massive useDeviceLocation
 * + map confirm labels. Falls back to city-only draft on failure.
 */
export const buildDeliveryLocationDraftFromCoords = async ({
  lat,
  lng,
  fallbackCity = '',
}) => {
  const localityDetail = await reverseGeocodeLatLng({ lat, lng });
  if (!isEmpty(localityDetail)) {
    return buildDeliveryLocationDraftFromLocalityDetail({
      localityDetail,
      lat,
      lng,
      fallbackCity,
    });
  }
  return buildLocationDraftFromGeocode({
    lat,
    lng,
    city: fallbackCity,
    formattedAddress: '',
    addressLines: [],
  });
};

export const formatGuestAddressLines = (address = {}) => {
  const parts = [
    get(address, 'addressLine1', '') || get(address, 'houseNo', ''),
    get(address, 'addressLine2', '') || get(address, 'street', '') || get(address, 'landmark', ''),
    get(address, 'localityName', '') || get(address, 'locality', '') || get(address, 'area', ''),
    get(address, 'localitySecondaryText', ''),
    get(address, 'city', '') || get(address, 'cityName', ''),
  ].filter((part) => !isEmpty(part));
  return parts;
};

/** First comma-separated segment of a geocode formatted address (street / place name). */
const firstStreetLineFromFormatted = (formatted = '') => {
  const text = String(formatted || '').trim();
  if (!text) {
    return '';
  }
  return text.split(',')[0].trim();
};

/**
 * Compact locality subline for Site Selected Outlet bar (home + menu).
 * Two sources only: selectedAddress or locationDraft (no outlet/city fallback).
 */
export const getDeliveryBarLocationLabel = ({ selectedAddress, locationDraft } = {}) => {
  if (!isEmpty(selectedAddress)) {
    const city =
      get(selectedAddress, 'city', '') || get(selectedAddress, 'cityName', '') || '';
    const line1 = get(selectedAddress, 'addressLine1', '');
    const locality =
      get(selectedAddress, 'localityName', '') ||
      get(selectedAddress, 'locality', '') ||
      get(selectedAddress, 'area', '');
    const lines = [line1, locality].filter((part) => !isEmpty(part));
    if (!isEmpty(lines)) {
      return { city, lines, primary: lines.join(', ') };
    }
    return {
      city,
      lines: [],
      primary: get(selectedAddress, 'formattedAddress', '') || city,
    };
  }

  if (!isEmpty(locationDraft)) {
    const city = get(locationDraft, 'city', '') || '';
    const draftLines = (get(locationDraft, 'addressLines', []) || []).filter(
      (part) => !isEmpty(part),
    );
    const primary =
      draftLines[0] ||
      firstStreetLineFromFormatted(get(locationDraft, 'formattedAddress', '')) ||
      '';
    return { city, lines: primary ? [primary] : [], primary };
  }

  return { city: '', lines: [], primary: '' };
};

/**
 * Outlet-bar Select Delivery Address CTA.
 * Shows when session flag is set (OOR / IP / cleared), or when delivery is bound
 * with neither a saved address nor a locality label (after draft cleared).
 * Hidden while contextual validate/label is pending so coords-only hydrate does
 * not flash the CTA before in-range reverse-geocode finishes.
 */
export const shouldShowSelectDeliveryAddressCta = ({
  darkStoreDeliveryEnabled,
  isDeliveryMode,
  hasBoundOutlet,
  needsSelectFlag = false,
  selectedAddress,
  locationDraft,
} = {}) => {
  if (!darkStoreDeliveryEnabled || !isDeliveryMode || !hasBoundOutlet) {
    return false;
  }
  if (needsSelectFlag || getNeedsSelectDeliveryAddress()) {
    return true;
  }
  if (!isEmpty(selectedAddress)) {
    return false;
  }
  // Coords-only draft during validate/label — avoid temporary CTA flicker.
  if (getNeedsContextualDeliveryValidate()) {
    return false;
  }
  const { primary } = getDeliveryBarLocationLabel({
    selectedAddress,
    locationDraft,
  });
  return isEmpty(primary);
};

/**
 * After in-range confirm: fill reverse-geocode labels on a coords-only hydrate draft.
 * Deep-link hydrate stores coords-only to avoid OOR reverse-geocode flash; label only
 * after range sync / ATC ensure proves the pin is servicable.
 */
export const labelCoordsOnlyDeliveryDraftIfNeeded = async () => {
  const draft = getDeliveryLocationDraft();
  const hasLabels =
    !isEmpty(get(draft, 'formattedAddress')) ||
    !isEmpty((get(draft, 'addressLines', []) || []).filter(Boolean));
  if (!hasLabels && !isEmpty(draft) && normalizeGuestLocation(draft)) {
    const labeled = await buildDeliveryLocationDraftFromCoords({
      lat: draft.lat,
      lng: draft.lng,
      fallbackCity: get(draft, 'city', '') || '',
    });
    setDeliveryLocationDraft(labeled);
  }
};

/**
 * After deep-link hydrate: validate address/draft against bound restaurant.
 * Out of range → needs Select Delivery Address CTA. Returns whether CTA is needed.
 * In range + coords-only draft → reverse-geocode locality for the outlet bar.
 */
export const syncContextualDeliveryRangeForOutlet = async ({
  restaurantId,
  selectedAddress,
  validateLocalities,
}) => {
  if (getNeedsSelectDeliveryAddress() && !getNeedsContextualDeliveryValidate()) {
    return true;
  }
  if (!getNeedsContextualDeliveryValidate()) {
    return getNeedsSelectDeliveryAddress();
  }
  if (isEmpty(restaurantId) || typeof validateLocalities !== 'function') {
    return getNeedsSelectDeliveryAddress();
  }

  const coords = getDeliveryLocationCoordsForValidate({
    selectedAddress,
    draft: getDeliveryLocationDraft(),
  });
  if (!coords) {
    clearNeedsContextualDeliveryValidate();
    setNeedsSelectDeliveryAddress(true);
    return true;
  }

  // Lazy require avoids circular import with darkStoreDelivery.
  // eslint-disable-next-line global-require
  const { validateCoordsForOutlet } = require('./darkStoreDelivery');
  const ok = await validateCoordsForOutlet({
    validateLocalities,
    restaurantId,
    lat: coords.lat,
    lng: coords.lng,
  });
  clearNeedsContextualDeliveryValidate();
  if (!ok) {
    setNeedsSelectDeliveryAddress(true);
    // Drop hydrate draft so OOR never flashes locality on the outlet bar.
    clearDeliveryLocationDraft();
    return true;
  }

  setNeedsSelectDeliveryAddress(false);
  // Label only after in-range — hydrate stores coords-only to skip OOR reverse-geocode.
  await labelCoordsOnlyDeliveryDraftIfNeeded();
  return false;
};

/** Primary + secondary lines for cart when map pin is set but guest address is not. */
export const getLocationDraftDisplay = (locationDraft) => {
  if (isEmpty(locationDraft)) {
    return { primary: '', secondary: '', hasDraft: false };
  }
  const lines = (get(locationDraft, 'addressLines', []) || []).filter(
    (part) => !isEmpty(part),
  );
  const city = get(locationDraft, 'city', '') || '';
  const formatted = get(locationDraft, 'formattedAddress', '') || '';
  let primary = lines[0] || city || '';
  let secondary = '';
  if (lines.length > 1) {
    secondary = lines.slice(1).join(', ');
  } else if (!isEmpty(formatted) && formatted !== primary) {
    if (isEmpty(primary)) {
      primary = formatted;
    } else {
      secondary = formatted;
    }
  } else if (!isEmpty(city) && city !== primary) {
    secondary = city;
  }
  if (isEmpty(primary) && !isEmpty(secondary)) {
    primary = secondary;
    secondary = '';
  }
  return {
    primary,
    secondary,
    hasDraft: !isEmpty(primary) || !isEmpty(get(locationDraft, 'lat')),
  };
};

/**
 * Seed map-footer labels from a bound address or location draft (no network).
 * Returns null when initial is coords-only.
 */
export const getDeliveryMapLabelsFromInitial = (initial) => {
  if (isEmpty(initial)) {
    return null;
  }

  const localityName =
    get(initial, 'localityName', '') ||
    get(initial, 'locality', '') ||
    get(initial, 'area', '');
  const addressLine1 = get(initial, 'addressLine1', '') || get(initial, 'houseNo', '');
  if (!isEmpty(localityName) || !isEmpty(addressLine1)) {
    const city =
      get(initial, 'city', '') || get(initial, 'cityName', '') || '';
    const primary = localityName || addressLine1;
    const secondary =
      get(initial, 'localitySecondaryText', '') ||
      (!isEmpty(city) && city !== primary ? city : '');
    return { primary, secondary, city };
  }

  const draftDisplay = getLocationDraftDisplay(initial);
  if (!isEmpty(draftDisplay.primary)) {
    return {
      primary: draftDisplay.primary,
      secondary: draftDisplay.secondary || '',
      city: get(initial, 'city', '') || '',
    };
  }

  return null;
};

export const mapValidateLocalitiesResponse = ({ response, addresses }) => {
  const validatedLocalityArr = get(response, 'data.locationCoordinates') || [];
  return (addresses || []).map((address) => {
    const lat = getAddressLat(address);
    const lng = getAddressLng(address);
    const match = validatedLocalityArr.find((locality) =>
      areCoordinatesEqual(
        get(locality, 'latitude'),
        get(locality, 'longitude'),
        lat,
        lng,
      ),
    );
    return {
      ...address,
      isServicable: match ? get(match, 'isServicable') : false,
    };
  });
};

export const isLocationServicableFromValidateResponse = (response) => {
  const coords = get(response, 'data.locationCoordinates', []) || [];
  if (isEmpty(coords)) {
    return false;
  }
  return coords.some((c) => get(c, 'isServicable') === true);
};

/** City / admin-area pins are too coarse for delivery (e.g. searching "Hyderabad"). */
const COARSE_MAP_PIN_TYPES = [
  'locality',
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
  'administrative_area_level_4',
  'administrative_area_level_5',
  'country',
  'colloquial_area',
];

const PRECISE_MAP_PIN_TYPES = [
  'street_address',
  'premise',
  'subpremise',
  'route',
  'neighborhood',
  'sublocality',
  'sublocality_level_1',
  'sublocality_level_2',
  'sublocality_level_3',
  'plus_code',
  'establishment',
  'point_of_interest',
  'park',
  'airport',
  'shopping_mall',
  'transit_station',
];

/** Reject city-scale viewports (km diagonal). Neighborhood pins are typically much smaller. */
const MAX_DELIVERY_PIN_VIEWPORT_DIAGONAL_KM = 12;

const getViewportDiagonalKm = (localityDetail) => {
  const viewport =
    get(localityDetail, 'geometry.viewport') || get(localityDetail, 'viewport');
  if (!viewport || typeof viewport.getNorthEast !== 'function') {
    return null;
  }
  try {
    const ne = viewport.getNorthEast();
    const sw = viewport.getSouthWest();
    if (!ne || !sw) {
      return null;
    }
    return checkDistanceBetweenCoordinates(
      {
        latitude1: ne.lat(),
        longitude1: ne.lng(),
        latitude2: sw.lat(),
        longitude2: sw.lng(),
      },
      false,
    );
  } catch (e) {
    return null;
  }
};

/**
 * True when Google place/geocode result is precise enough to be a delivery pin.
 * City-only Autocomplete picks (e.g. "Hyderabad") fail even if validateLocalities is true.
 */
export const isDeliveryMapPinPreciseEnough = (localityDetail = {}) => {
  if (isEmpty(localityDetail)) {
    return false;
  }

  const types = get(localityDetail, 'types', []) || [];
  if (!isEmpty(types)) {
    if (types.some((type) => PRECISE_MAP_PIN_TYPES.includes(type))) {
      const diagonalKm = getViewportDiagonalKm(localityDetail);
      if (diagonalKm != null && diagonalKm > MAX_DELIVERY_PIN_VIEWPORT_DIAGONAL_KM) {
        return false;
      }
      return true;
    }
    const meaningfulTypes = types.filter((type) => type !== 'political');
    if (
      !isEmpty(meaningfulTypes) &&
      meaningfulTypes.every((type) => COARSE_MAP_PIN_TYPES.includes(type))
    ) {
      return false;
    }
  }

  const diagonalKm = getViewportDiagonalKm(localityDetail);
  if (diagonalKm != null && diagonalKm > MAX_DELIVERY_PIN_VIEWPORT_DIAGONAL_KM) {
    return false;
  }

  // Drag reverse-geocode without clear types / viewport — allow; zone check still applies.
  return true;
};
